import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useFetch from "hooks/useFetch";
import {
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  SegmentedControl,
  Select,
  Skeleton,
  Table,
  TextField,
} from "stories/Admin";
import styles from "./MosqueStatsPage.module.scss";

/*
 * Superadmin: hvor mange app-installasjoner som har valgt hver moské, og hvor mange som følger
 * moskeens push. Tallene kommer fra appene (anonym device-id), og bare installasjoner som har
 * vært aktive de siste N dagene telles.
 */

interface MosqueStat {
  orgNr: string;
  name: string;
  city: string | null;
  selectedCount: number;
  subscribedCount: number;
}

interface MosqueStats {
  days: number;
  activeDevices: number;
  mosques: MosqueStat[];
  generatedAt: string;
}

type SortKey = "subscribedCount" | "selectedCount" | "name";

const MosqueStatsPage: React.FC = () => {
  const { execute } = useFetch<MosqueStats>();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [days, setDays] = useState(90);
  const [data, setData] = useState<MosqueStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("subscribedCount");

  const load = useCallback(async (d: number) => {
    setIsLoading(true);
    const result = await executeRef.current(
      "/api/v1/admin/mosque-stats",
      "GET",
      undefined,
      { days: d },
    );
    if (result) setData(result);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data?.mosques || []).filter(
      (m) =>
        !q ||
        m.name.toLowerCase().includes(q) ||
        m.orgNr.includes(q) ||
        (m.city || "").toLowerCase().includes(q),
    );
    return [...list].sort((a, b) =>
      sortKey === "name"
        ? a.name.localeCompare(b.name, "nb")
        : b[sortKey] - a[sortKey] || a.name.localeCompare(b.name, "nb"),
    );
  }, [data, search, sortKey]);

  const totalSelected = (data?.mosques || []).reduce(
    (s, m) => s + m.selectedCount,
    0,
  );
  const totalSubscribed = (data?.mosques || []).reduce(
    (s, m) => s + m.subscribedCount,
    0,
  );

  const nf = (n: number) => n.toLocaleString("nb-NO");

  const columns: Array<Column<MosqueStat>> = [
    { key: "name", header: "Moské", render: (m) => <b>{m.name}</b> },
    { key: "city", header: "By", render: (m) => m.city || "–" },
    {
      key: "orgNr",
      header: "Org.nr.",
      numeric: true,
      render: (m) => <span className={styles.muted}>{m.orgNr}</span>,
    },
    {
      key: "selected",
      header: "Valgt som min moské",
      numeric: true,
      align: "right",
      render: (m) => nf(m.selectedCount),
    },
    {
      key: "subscribed",
      header: "Følger push",
      align: "right",
      render: (m) => (
        <Badge tone={m.subscribedCount > 0 ? "mint" : "grey"}>
          {nf(m.subscribedCount)}
        </Badge>
      ),
    },
  ];

  const stats: Array<[string, React.ReactNode]> = [
    ["Aktive installasjoner", data ? nf(data.activeDevices) : "–"],
    ["Har valgt en moské", data ? nf(totalSelected) : "–"],
    ["Abonnementer totalt", data ? nf(totalSubscribed) : "–"],
    [
      "Moskeer med følgere",
      data ? nf(data.mosques.filter((m) => m.subscribedCount > 0).length) : "–",
    ],
  ];

  return (
    <>
      <PageHeader
        title="Moské-statistikk"
        subtitle="Hvor mange app-installasjoner som har valgt hver moské, og hvor mange som følger moskeens push."
        actions={
          <>
            <Select
              aria-label="Periode"
              fieldClassName={styles.period}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={7}>Siste 7 dager</option>
              <option value={30}>Siste 30 dager</option>
              <option value={90}>Siste 90 dager</option>
              <option value={365}>Siste år</option>
            </Select>
            <Button
              variant="secondary"
              icon={<Icon name="swap" size={16} />}
              onClick={() => load(days)}
              loading={isLoading}
            >
              Oppdater
            </Button>
          </>
        }
      />

      <div className={styles.stats}>
        {stats.map(([label, value]) => (
          <div key={label} className={styles.stat}>
            <b>{value}</b>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <div className={styles.filters}>
        <TextField
          aria-label="Søk"
          type="search"
          placeholder="Søk etter moské, by eller org.nr."
          className={styles.search}
          fieldClassName={styles.searchField}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className={styles.muted}>Sorter:</span>
        <SegmentedControl
          label="Sorter"
          value={sortKey}
          onChange={setSortKey}
          options={[
            { value: "subscribedCount", label: "Følgere" },
            { value: "selectedCount", label: "Valgt" },
            { value: "name", label: "Navn" },
          ]}
        />
      </div>

      <Card className={styles.tableCard}>
        {isLoading && !data ? (
          <div className={styles.skeletons}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} height={36} />
            ))}
          </div>
        ) : (
          <Table
            columns={columns}
            rows={rows}
            rowKey={(m) => m.orgNr}
            empty={
              <EmptyState icon="chart" title="Ingen data ennå">
                Tallene kommer når brukerne åpner den nye app-versjonen.
              </EmptyState>
            }
          />
        )}
      </Card>
      {data && (
        <span className={styles.muted}>
          Teller installasjoner som har vært aktive de siste {data.days} dagene.
        </span>
      )}
    </>
  );
};

export default MosqueStatsPage;
